/**
 * All site copy, in one typed place. Sections import from here and never hard-code text.
 *
 * Positioning: Vokarta is an AI business companion for the world’s small businesses, the
 * assistant you can simply call (see docs/DESIGN.md). It is not a receptionist, a chatbot,
 * a calling platform or an automation agency.
 *
 * Headlines are arrays of {@link Segment}s. `em: true` marks the warm accent words, which
 * render in the serif accent face, Boska italic (see `<Segments>` in components/ui). Use
 * {@link toPlainText} for `aria-label`s, titles and SplitText fallbacks.
 *
 * Honesty rules: never claim build times, never invent customers, testimonials or metrics,
 * label every showcase site "Concept", and always show `call.label` ("Illustrative
 * conversation") next to the sample call.
 *
 * Romanised Hindi lines are marked `hi-Latn`. Put that on the element's `lang` attribute
 * so screen readers pronounce them properly.
 *
 * @module
 */

/** A run of headline text. `em` marks the serif-italic accent words. */
export interface Segment {
  readonly text: string
  readonly em?: boolean
}

/** A headline made of segments. */
export type Headline = readonly Segment[]

/** A navigation or footer link. */
export interface NavLink {
  readonly label: string
  readonly href: string
  readonly external?: boolean
}

/** Joins segments into plain text, e.g. for `aria-label` or `document.title`. */
export function toPlainText(headline: Headline): string {
  return headline.map((segment) => segment.text).join('')
}

const TELEGRAM_URL: string = import.meta.env.VITE_TELEGRAM_URL || 'https://t.me/vayu09bot'

// ---------------------------------------------------------------------------------------
// Site and navigation
// ---------------------------------------------------------------------------------------

export const site = {
  name: 'Vokarta',
  tagline: 'Your business. Your assistant. Always available.',
  motto: 'The voice that gets it done.',
  description:
    'Vokarta is the AI business companion for the world’s shopkeepers. Call or send a voice note in your own language, and Vokarta builds your website, runs your WhatsApp catalog and Google profile, posts your Reels, follows up with customers and tells you how the day went.',
  telegramUrl: TELEGRAM_URL,
  year: 2026,
} as const

const NAV_LINKS = [
  { label: 'How it works', href: '#pipeline' },
  { label: 'What it does', href: '#storefront' },
  { label: 'Identity lab', href: '#identity' },
  { label: 'Work', href: '#showcase' },
  { label: 'Markets', href: '#markets' },
] as const satisfies readonly NavLink[]

export const nav = {
  links: NAV_LINKS,
  cta: { label: 'Talk to Vokarta', href: TELEGRAM_URL, external: true },
  /** Interface strings: the landmark's `aria-label` and the mobile menu toggle. */
  ui: { label: 'Main', menu: 'Menu', close: 'Close' },
} as const satisfies {
  links: readonly NavLink[]
  cta: NavLink
  ui: { label: string; menu: string; close: string }
}

// ---------------------------------------------------------------------------------------
// Sections, in page order
// ---------------------------------------------------------------------------------------

/** A word in another language, with its BCP 47 code for the `lang` attribute. */
export interface LangWord {
  readonly text: string
  readonly lang: string
}

export const hero = {
  eyebrow: 'AI business companion',
  /** Two lines: "Talk." / "Don’t *type.*" */
  title: [[{ text: 'Talk.' }], [{ text: 'Don’t ' }, { text: 'type.', em: true }]],
  /**
   * Decorative language cycling for "Talk." (aria-hidden; the H1 stays the real text).
   * Wrap each word in `<Lang code={word.lang}>`; Devanagari picks up Anek Devanagari.
   */
  speakWords: [
    { text: 'Talk.', lang: 'en' },
    { text: 'बोलो।', lang: 'hi' },
    { text: 'Bicara.', lang: 'id' },
    { text: 'Habla.', lang: 'es' },
    { text: 'Fala.', lang: 'pt' },
    { text: 'Ongea.', lang: 'sw' },
  ],
  kicker: 'Your business. Your assistant. Always available.',
  lead: 'Vokarta is the assistant every small business can simply call. Say what you need in your own language. Vokarta gets it done, from your website to your next customer.',
  primary: { label: 'Talk to Vokarta', href: TELEGRAM_URL, external: true },
  secondary: { label: 'How it works', href: '#pipeline' },
  meta: ['Early access · India', 'Vox + kartā: the voice that does', 'Scroll'],
  holdToTalk: {
    idle: 'Hold to talk',
    active: 'Listening · stays on your device',
    denied: 'Mic unavailable, so the orb will talk for you',
  },
  /** Screen-reader description of the 3D scene (the canvas itself is aria-hidden). */
  sceneLabel:
    'A clear glass orb that breathes and ripples softly, as if speaking, in front of the word Vokarta.',
} as const satisfies {
  eyebrow: string
  title: readonly Headline[]
  speakWords: readonly LangWord[]
  kicker: string
  lead: string
  primary: NavLink
  secondary: NavLink
  meta: readonly string[]
  holdToTalk: { idle: string; active: string; denied: string }
  sceneLabel: string
}

/** A counter in the manifesto stats row: `value` + `suffix`, then `label`. */
export interface Stat {
  readonly value: number
  readonly suffix: string
  readonly label: string
}

export const manifesto = {
  eyebrow: '01 · Why',
  body: 'Voice is replacing the keyboard for people at desks. Vibe coding still needs a laptop and a prompt. But most of the world’s businesses run on something simpler: a phone call to someone they trust to get the job done. A kartā. Technology should adapt to people, not the other way around.',
  /** Exact substrings of `body` that switch to the iris serif italic as they light up. */
  highlights: ['a phone call', 'get the job done', 'adapt to people'],
  /** The word behind the name: Devanagari (`lang="hi"`), transliteration, gloss. */
  karta: { deva: 'कर्ता', latin: 'kartā', gloss: 'the one who gets it done' },
  /** "Traditional software" versus "Vokarta", one row per pair. */
  versus: {
    from: 'Traditional software',
    to: 'Vokarta',
    title: [{ text: 'Not another ' }, { text: 'dashboard.', em: true }],
    rows: [
      ['Open a dashboard', 'Make a phone call'],
      ['Learn ten different tools', 'Talk naturally'],
      ['Do every task yourself', 'Delegate it'],
      ['Switch between platforms', 'One assistant that knows your business'],
      ['Figure out software workflows', 'Explain what you need'],
      ['Work around the software', 'Software that adapts to you'],
    ],
  },
  stats: [
    { value: 6, suffix: ' crore', label: 'small businesses in India' },
    { value: 400, suffix: 'M+', label: 'across emerging markets' },
    { value: 0, suffix: '', label: 'dashboards, forms or prompts to learn' },
  ],
  closing:
    'We aren’t building another dashboard for business owners. We’re building the assistant they can simply talk to.',
} as const satisfies {
  eyebrow: string
  body: string
  highlights: readonly string[]
  karta: { deva: string; latin: string; gloss: string }
  versus: {
    from: string
    to: string
    title: Headline
    rows: readonly (readonly [from: string, to: string])[]
  }
  stats: readonly Stat[]
  closing: string
}

/** One line of the sample call. `hi` is romanised Hindi (`lang="hi-Latn"`); `en` is the subtitle. */
export interface DialogueLine {
  readonly speaker: 'owner' | 'vokarta'
  readonly hi: string
  readonly en: string
}

/** A real-style request for the multilingual marquee. */
export interface CallRequest {
  readonly text: string
  /** Language name to display, e.g. "Bahasa Indonesia". */
  readonly lang: string
  /** BCP 47 code for the element's `lang` attribute, e.g. `id`, `hi-Latn`. */
  readonly langCode: string
  /** English translation; empty when the request is already in English. */
  readonly en: string
}

export const call = {
  eyebrow: '02 · The call',
  title: [{ text: 'It feels like a ' }, { text: 'person.', em: true }, { text: ' Not software.' }],
  lead: 'Vokarta remembers your shop, your customers and what you discussed yesterday, so every call picks up where the last one left off.',
  /** Must be visible next to the dialogue: the numbers in it are illustrative. */
  label: 'Illustrative conversation',
  /** The caller shown on the call card. */
  caller: 'Vokarta',
  /** Speaker labels on the captions. The visitor stands in for the shop owner. */
  speakers: { owner: 'You', vokarta: 'Vokarta' },
  /** Labels for the marquee's pause/play control (WCAG 2.2.2). */
  marqueeControls: { pause: 'Pause the ticker', play: 'Play the ticker' },
  dialogue: [
    {
      speaker: 'owner',
      hi: 'Vokarta, aaj dukaan ka kya haal hai?',
      en: 'Vokarta, how’s the shop doing today?',
    },
    {
      speaker: 'vokarta',
      hi: 'Aaj ₹12,500 ki sale hui, kal se 15% zyada. Teen enquiries ka follow-up baaki hai. Unhe call kar doon?',
      en: '₹12,500 in sales today, 15% more than yesterday. Three enquiries still need a follow-up. Shall I call them?',
    },
    {
      speaker: 'owner',
      hi: 'Haan, kar do. Aur kal ke liye ek special offer bana do.',
      en: 'Yes, go ahead. And make a special offer for tomorrow.',
    },
    {
      speaker: 'vokarta',
      hi: 'Bilkul. Kaju katli pe 10% off ka post ready hai. Aap haan bolo, toh Instagram pe live.',
      en: 'Sure. A 10% off post for kaju katli is ready. Say yes and it goes live on Instagram.',
    },
    { speaker: 'owner', hi: 'Haan, daal do.', en: 'Yes, post it.' },
  ],
  /** Task chips that resolve as the call goes on. */
  tasks: ['Calling 3 customers', 'Offer post ready', 'Posted to Instagram'],
  /** Multilingual marquee of the kind of things owners ask. */
  requests: [
    {
      text: 'Aaj kitni sale hui?',
      lang: 'Hindi',
      langCode: 'hi-Latn',
      en: 'How much did I sell today?',
    },
    {
      text: 'Diwali ke 3 Reels bana do.',
      lang: 'Hindi',
      langCode: 'hi-Latn',
      en: 'Make three Diwali Reels.',
    },
    {
      text: 'Kal wale customer ko call karo.',
      lang: 'Hindi',
      langCode: 'hi-Latn',
      en: 'Call yesterday’s customer back.',
    },
    {
      text: 'Kal supplier ki payment yaad dila dena.',
      lang: 'Hindi',
      langCode: 'hi-Latn',
      en: 'Remind me to pay the supplier tomorrow.',
    },
    {
      text: 'Buatkan website untuk toko saya.',
      lang: 'Bahasa Indonesia',
      langCode: 'id',
      en: 'Make a website for my shop.',
    },
    {
      text: '¿Por qué bajaron mis ventas esta semana?',
      lang: 'Spanish',
      langCode: 'es',
      en: 'Why did my sales drop this week?',
    },
    {
      text: 'Cria um site pra minha padaria.',
      lang: 'Portuguese',
      langCode: 'pt-BR',
      en: 'Make a site for my bakery.',
    },
    {
      text: 'Nisaidie kupata wateja zaidi.',
      lang: 'Swahili',
      langCode: 'sw',
      en: 'Help me get more customers.',
    },
    { text: 'I want more customers this month.', lang: 'English', langCode: 'en', en: '' },
  ],
  underTheHood: {
    eyebrow: 'Under the hood',
    title: [{ text: 'Our own ' }, { text: 'voice stack.', em: true }],
    body: 'Real-time speech recognition, reasoning with tools, and natural speech, built for Indian languages first. The same infrastructure will power developers too: APIs and SDKs are on the roadmap.',
    /** Signal-flow band, left to right; a pulse travels along it. */
    flow: ['Voice', 'Speech-to-text', 'Reasoning + tools', 'Text-to-speech', 'Voice'],
  },
} as const satisfies {
  eyebrow: string
  title: Headline
  lead: string
  label: string
  caller: string
  speakers: Record<DialogueLine['speaker'], string>
  marqueeControls: { pause: string; play: string }
  dialogue: readonly DialogueLine[]
  tasks: readonly string[]
  requests: readonly CallRequest[]
  underTheHood: { eyebrow: string; title: Headline; body: string; flow: readonly string[] }
}

/**
 * The particle form each pipeline step morphs into:
 * `wave` waveform ring, `mind` constellation, `qr` UPI-style QR grid,
 * `site` exploded website layers plus a phone.
 */
export type PipelineShape = 'wave' | 'mind' | 'qr' | 'site'

/** Ids of the four pipeline steps. */
export type PipelineStepId = 'call' | 'understand' | 'agree' | 'done'

/** One step of "How it works". Only the first step carries a sample quote. */
export interface PipelineStep {
  readonly id: PipelineStepId
  readonly index: string
  readonly title: string
  readonly body: string
  readonly shape: PipelineShape
  /** Sample request in romanised Hindi (`lang="hi-Latn"`). */
  readonly quote?: string
  /** English translation of `quote`. */
  readonly quoteEn?: string
}

const PIPELINE_STEPS: readonly PipelineStep[] = [
  {
    id: 'call',
    index: '01',
    title: 'Call',
    body: 'Call Vokarta or send a voice note in Hindi, Hinglish or your own dialect. Explain what you need the way you’d tell a friend.',
    shape: 'wave',
    quote:
      '“Meri Raipur mein sweets ki dukaan hai, online order wala page aur Diwali ke 3 Reels chahiye.”',
    quoteEn:
      '“I have a sweet shop in Raipur. I need an online ordering page and three Diwali Reels.”',
  },
  {
    id: 'understand',
    index: '02',
    title: 'Understand',
    body: 'While you talk, Vokarta picks out what matters: shop name, items, prices, location and what you want built. Then it scopes the job in real time.',
    shape: 'mind',
  },
  {
    id: 'agree',
    index: '03',
    title: 'Agree',
    body: 'No fixed plans. Vokarta quotes a fair price for the job right on the call, and you can bargain. It sends a UPI link on WhatsApp before you hang up.',
    shape: 'qr',
  },
  {
    id: 'done',
    index: '04',
    title: 'Done',
    body: 'Once you’ve paid, Vokarta builds and launches everything: storefront, WhatsApp catalog and Reels. Then it keeps it all running. Want a change? Just call.',
    shape: 'site',
  },
]

export const pipeline = {
  eyebrow: '03 · How it works',
  title: [{ text: 'One call. Everything ' }, { text: 'after it.', em: true }],
  steps: PIPELINE_STEPS,
  /** Screen-reader description of the particle scene (the canvas itself is aria-hidden). */
  sceneLabel:
    'Thousands of dots that form a voice-note ring, then a constellation, then a payment QR code, then a website and a phone.',
} as const satisfies {
  eyebrow: string
  title: Headline
  steps: readonly PipelineStep[]
  sceneLabel: string
}

export const storefront = {
  eyebrow: '04 · What it does',
  title: [{ text: 'One call. Everything your ' }, { text: 'business needs.', em: true }],
  lead: 'Vokarta works out what your shop needs, builds exactly that and keeps it running, so you can get back to your customers.',
  /** Orbit the diorama, in two rings if needed. */
  capabilities: [
    {
      id: 'website',
      title: 'Website & storefront',
      body: 'A fast, beautiful site with click-to-order and UPI payments.',
    },
    {
      id: 'whatsapp',
      title: 'WhatsApp catalog',
      body: 'Customers browse and order in the chat they already use.',
    },
    {
      id: 'google',
      title: 'Google Business Profile',
      body: 'Show up when the neighbourhood searches, and keep reviews answered.',
    },
    {
      id: 'social',
      title: 'Instagram & Reels',
      body: 'Fresh posts and Reels made from your own photos.',
    },
    {
      id: 'customers',
      title: 'Customer follow-ups',
      body: 'Every enquiry remembered, every lead called back.',
    },
    {
      id: 'ads',
      title: 'Offers & local ads',
      body: 'Campaigns that reach your street, not the whole planet.',
    },
    {
      id: 'insights',
      title: 'Business insights',
      body: 'Ask how the day went. Get a straight answer.',
    },
    {
      id: 'automations',
      title: 'Automations',
      body: 'Reminders, supplier payments and the busywork, handled.',
    },
  ],
  shopSign: 'Sharma Sweets',
  sceneLabel:
    'Sharma Sweets, a pastel corner sweet shop that opens its shutter and lights up as you scroll.',
} as const satisfies {
  eyebrow: string
  title: Headline
  lead: string
  capabilities: readonly { id: string; title: string; body: string }[]
  shopSign: string
  sceneLabel: string
}

/** One of the eight things Vokarta does. */
export type Capability = (typeof storefront.capabilities)[number]
/** `'website' | 'whatsapp' | 'google' | 'social' | 'customers' | 'ads' | 'insights' | 'automations'` */
export type CapabilityId = Capability['id']

export const identity = {
  eyebrow: '05 · Identity lab',
  title: [{ text: 'No two shops ' }, { text: 'look alike.', em: true }],
  lead: 'Type a shop’s name. Our design engine turns it into an identity with a mark, palette and pattern, live in your browser.',
  placeholder: 'Your shop’s name',
  defaultName: 'Sharma Sweets',
  presets: [
    'Sharma Sweets',
    'Noor Chikan',
    'Raipur Hardware',
    'Toko Melati',
    'Mama Nkechi',
    'Padaria Estrela',
  ],
  actions: { shuffle: 'Shuffle', download: 'Download PNG' },
  note: 'A live generative sketch. Every name is a seed.',
  /** Lab interface strings. `{name}` and `{pattern}` are filled by `fill()` from `@/lib/template`. */
  ui: {
    inputLabel: 'Shop name',
    presetsLabel: 'Try a shop',
    boardLabel:
      'Identity board for “{name}”: a voice-rosette mark, a five-colour palette and a {pattern} pattern band.',
    status: 'New identity for {name}.',
    readout: { seed: 'Seed', pattern: 'Pattern', type: 'Type', palette: 'Palette' },
    patterns: { truchet: 'Truchet', halftone: 'Halftone', woven: 'Woven' },
    fonts: { anek: 'Anek Devanagari', mono: 'Geist Mono' },
    fileSuffix: 'identity',
  },
} as const satisfies {
  eyebrow: string
  title: Headline
  lead: string
  placeholder: string
  defaultName: string
  presets: readonly string[]
  actions: { shuffle: string; download: string }
  note: string
  ui: {
    inputLabel: string
    presetsLabel: string
    boardLabel: string
    status: string
    readout: { seed: string; pattern: string; type: string; palette: string }
    patterns: { truchet: string; halftone: string; woven: string }
    fonts: { anek: string; mono: string }
    fileSuffix: string
  }
}

/** A concept site in the showcase gallery. Always labelled "Concept". */
export interface ShowcaseProject {
  readonly id: string
  readonly name: string
  readonly city: string
  readonly country: string
  readonly trade: string
  readonly hook: string
}

export const showcase = {
  eyebrow: '06 · Work',
  title: [{ text: 'Made for ' }, { text: 'real streets.', em: true }],
  lead: 'Concept sites in the styles Vokarta builds, each one designed around a real kind of shop.',
  label: 'Concept',
  /** Accessible name of each mini-site preview; `{name}`, `{city}` and `{hook}` are filled in. */
  previewLabel: 'Concept website for {name} in {city}: “{hook}”',
  projects: [
    {
      id: 'sharma-sweets',
      name: 'Sharma Sweets',
      city: 'Indore',
      country: 'India',
      trade: 'Mithai & namkeen',
      hook: 'Pure ghee. Made fresh every morning.',
    },
    {
      id: 'noor-chikan',
      name: 'Noor Chikan',
      city: 'Lucknow',
      country: 'India',
      trade: 'Hand-embroidered chikankari',
      hook: 'Threadwork from the old city.',
    },
    {
      id: 'raipur-hardware',
      name: 'Raipur Hardware Co.',
      city: 'Raipur',
      country: 'India',
      trade: 'Tools, paint & fittings',
      hook: 'If it’s broken, we’ve got the part.',
    },
    {
      id: 'toko-melati',
      name: 'Toko Batik Melati',
      city: 'Yogyakarta',
      country: 'Indonesia',
      trade: 'Hand-drawn batik',
      hook: 'Batik tulis, drawn by hand.',
    },
    {
      id: 'mama-nkechi',
      name: 'Mama Nkechi’s Kitchen',
      city: 'Lagos',
      country: 'Nigeria',
      trade: 'Jollof & small chops',
      hook: 'Party jollof, every day.',
    },
    {
      id: 'amani-fresh',
      name: 'Amani Fresh',
      city: 'Nairobi',
      country: 'Kenya',
      trade: 'Fruit & vegetables',
      hook: 'From the farm to your street.',
    },
    {
      id: 'padaria-estrela',
      name: 'Padaria Estrela',
      city: 'São Paulo',
      country: 'Brazil',
      trade: 'Bakery & café',
      hook: 'Pão quentinho toda manhã.',
    },
    {
      id: 'el-guero',
      name: 'Taquería El Güero',
      city: 'Mexico City',
      country: 'Mexico',
      trade: 'Tacos al pastor',
      hook: 'Al pastor hasta las 2 a.m.',
    },
  ],
} as const satisfies {
  eyebrow: string
  title: Headline
  lead: string
  label: string
  previewLabel: string
  projects: readonly ShowcaseProject[]
}

/** `'sharma-sweets' | 'noor-chikan' | …` */
export type ShowcaseProjectId = (typeof showcase.projects)[number]['id']

/** Microcopy inside one concept mini-site: generic kinds of goods, never prices or claims. */
export interface MiniSiteCopy {
  /** BCP 47 code for the mini-site's own text. */
  readonly lang: string
  readonly nav: readonly [string, string, string]
  readonly products: readonly [string, string, string]
  /** The "Order on chat" button, in the shop's language. */
  readonly cta: string
  /** The shop's name in its own script, when it has one. */
  readonly script?: { readonly text: string; readonly lang: string }
}

/**
 * Per-site microcopy for the showcase's concept mini-sites, keyed by project id. It sits
 * outside `showcase` because its keys come from `showcase.projects`.
 */
export const showcaseSites = {
  'sharma-sweets': {
    lang: 'en-IN',
    nav: ['Mithai', 'Namkeen', 'Visit'],
    products: ['Kaju katli', 'Motichoor laddoo', 'Soan papdi'],
    cta: 'Order on chat',
    script: { text: 'शर्मा स्वीट्स', lang: 'hi' },
  },
  'noor-chikan': {
    lang: 'en-IN',
    nav: ['Kurtas', 'Dupattas', 'Our craft'],
    products: ['Kurta', 'Dupatta', 'Anarkali'],
    cta: 'Order on chat',
  },
  'raipur-hardware': {
    lang: 'en-IN',
    nav: ['Tools', 'Paint', 'Fittings'],
    products: ['Hammers', 'Paint', 'Fasteners'],
    cta: 'Order on chat',
    script: { text: 'रायपुर हार्डवेयर', lang: 'hi' },
  },
  'toko-melati': {
    lang: 'id',
    nav: ['Koleksi', 'Proses', 'Kontak'],
    products: ['Kain batik', 'Kemeja', 'Selendang'],
    cta: 'Pesan lewat chat',
  },
  'mama-nkechi': {
    lang: 'en-NG',
    nav: ['Menu', 'Party trays', 'Find us'],
    products: ['Jollof rice', 'Small chops', 'Puff-puff'],
    cta: 'Order on chat',
  },
  'amani-fresh': {
    lang: 'en-KE',
    nav: ['Fruit', 'Vegetables', 'Delivery'],
    products: ['Mangoes', 'Avocados', 'Sukuma wiki'],
    cta: 'Order on chat',
  },
  'padaria-estrela': {
    lang: 'pt-BR',
    nav: ['Pães', 'Doces', 'Café'],
    products: ['Pão francês', 'Pão de queijo', 'Sonho'],
    cta: 'Peça pelo chat',
  },
  'el-guero': {
    lang: 'es-MX',
    nav: ['Tacos', 'Salsas', 'Horario'],
    products: ['Al pastor', 'Suadero', 'Campechano'],
    cta: 'Pide por chat',
  },
} as const satisfies Record<ShowcaseProjectId, MiniSiteCopy>

/** A market on the globe. `wave` is the expansion order (1 = now). */
export interface Market {
  readonly id: string
  readonly country: string
  readonly city: string
  readonly lat: number
  readonly lon: number
  readonly wave: 1 | 2 | 3 | 4
  readonly statusLabel: string
}

export const markets = {
  eyebrow: '07 · Where',
  title: [{ text: 'India first. Then ' }, { text: 'everyone else.', em: true }],
  lead: 'Six crore small businesses in India. More than 400 million across the markets we’re building for next, in their own languages.',
  list: [
    {
      id: 'in',
      country: 'India',
      city: 'Raipur',
      lat: 21.2514,
      lon: 81.6296,
      wave: 1,
      statusLabel: 'Now',
    },
    {
      id: 'id',
      country: 'Indonesia',
      city: 'Jakarta',
      lat: -6.2088,
      lon: 106.8456,
      wave: 2,
      statusLabel: 'Next',
    },
    {
      id: 'ng',
      country: 'Nigeria',
      city: 'Lagos',
      lat: 6.5244,
      lon: 3.3792,
      wave: 3,
      statusLabel: 'Then',
    },
    {
      id: 'ke',
      country: 'Kenya',
      city: 'Nairobi',
      lat: -1.2921,
      lon: 36.8219,
      wave: 3,
      statusLabel: 'Then',
    },
    {
      id: 'br',
      country: 'Brazil',
      city: 'São Paulo',
      lat: -23.5505,
      lon: -46.6333,
      wave: 4,
      statusLabel: 'Later',
    },
    {
      id: 'mx',
      country: 'Mexico',
      city: 'Mexico City',
      lat: 19.4326,
      lon: -99.1332,
      wave: 4,
      statusLabel: 'Later',
    },
  ],
  /** Arcs radiate from here. */
  origin: { city: 'Raipur', lat: 21.2514, lon: 81.6296 },
  sceneLabel:
    'A globe with arcs from Raipur to Jakarta, Lagos, Nairobi, São Paulo and Mexico City.',
} as const satisfies {
  eyebrow: string
  title: Headline
  lead: string
  list: readonly Market[]
  origin: { city: string; lat: number; lon: number }
  sceneLabel: string
}

/** `'in' | 'id' | 'ng' | 'ke' | 'br' | 'mx'` */
export type MarketId = (typeof markets.list)[number]['id']

export const cta = {
  eyebrow: '08 · Start',
  /** Romanised Hindi (`lang="hi-Latn"`). */
  kicker: 'Arre, chinta mat kar.',
  /** Romanised Hindi (`lang="hi-Latn"`). */
  title: [{ text: 'Vokarta ko ' }, { text: 'call laga.', em: true }],
  /** English translation of kicker + title. */
  translation: '“Don’t worry, just call Vokarta.”',
  lead: 'Tell it what your business needs. It takes it from there.',
  button: { label: 'Start on Telegram', href: TELEGRAM_URL, external: true },
  note: 'Early access runs on Telegram today · the phone line is coming',
} as const satisfies {
  eyebrow: string
  kicker: string
  title: Headline
  translation: string
  lead: string
  button: NavLink
  note: string
}

export const footer = {
  wordmark: 'VOKARTA',
  line: 'Your business. Your assistant. Always available.',
  closing:
    'We aren’t building another dashboard. We’re building the assistant you can simply talk to.',
  madeIn: 'Made in India, for everyone else.',
  links: [...NAV_LINKS, { label: 'Telegram', href: TELEGRAM_URL, external: true }],
  legal: '© 2026 Vokarta',
  backToTop: 'Back to top',
} as const satisfies {
  wordmark: string
  line: string
  closing: string
  madeIn: string
  links: readonly NavLink[]
  legal: string
  backToTop: string
}
